/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, MouseEvent } from 'react';
import {useAuth} from "../auth/AuthContext"


// Define the types
type Order = 'ASC' | 'DESC' | null;

interface Row {
  user: string;
  project: string;
  event: string;
  details: string;
  timestamp: string;
}

interface LogbookContextType {
  rows: Row[];
  filteredRows: Row[];
  order: Order;
  orderBy: string | null;
  handleSort: (event: MouseEvent<unknown>, property: string) => void;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  uniqueUsers: string[];
  uniqueProjects: string[];
  uniqueEvents: string[];
  sortedTimestamps: string[];
  fetchData: (filters?: Filters) => void;
  localFilters: Filters;
  setLocalFilters: React.Dispatch<React.SetStateAction<Filters>>;
  setWasFiltered: React.Dispatch<React.SetStateAction<boolean>>;
  wasFiltered: boolean;
  fetchAllData: () => void;
  allData: Row[];
  loading: boolean; 
}

interface Filters {
  user: string;
  project: string;
  event: string;
  from: string;
  to: string;
  details: string;
}

const LogbookContext = createContext<LogbookContextType | undefined>(undefined);

const LogbookProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [allData, setAllData] =  useState<Row[]>([]);
  const [order, setOrder] = useState<Order>('ASC');
  const [orderBy, setOrderBy] = useState<string | null>('user');
  const [filters, setFilters] = useState<Filters>({
    user: '',
    project: '',
    event: '',
    from: '',
    to: '',
    details: ''
  });
  const [localFilters, setLocalFilters] = useState<Filters>(filters);
  const [wasFiltered, setWasFiltered] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const {secureFetch, role} = useAuth()


  const fetchData = async (filters: Filters = {
    user: '',
    project: '',
    event: '',
    from: '',
    to: '',
    details: ''
  }) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        const value = (filters as any)[key];
        if (value) {
          queryParams.append(key, value);
        }
      });
      if(role){
        const response = await secureFetch(`/api/${role}/logs?${queryParams.toString()}`, 
        {
          method: 'GET',
          headers: {
              'Content-Type': 'application/json',
          },
        }
        );
        const data = await response.json();
        const orderedData = orderData(data);

        // Only update state if data is different
        setRows((prevRows) =>
          JSON.stringify(prevRows) === JSON.stringify(orderedData) ? prevRows : orderedData
        );
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // End loading
    }
  };


  const fetchAllData = async () => {
    setLoading(true); // Start loading

    try {
      if (role) {
        const response = await secureFetch(`/api/${role}/logs`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        const orderedData = orderData(data)
        setAllData(orderedData);
      }
    } catch (error) {
      console.error('Error fetching all data:', error);
    } finally {
      setLoading(false); // End loading
   }
  };

  const orderData = (data: Row[]): Row[] => {
    return data.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };


  useEffect(() => {
    fetchData(filters);
  }, [filters])

  

  const handleSort = (event: MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'ASC';
    const isDesc = orderBy === property && order === 'DESC';
    if (isAsc) {
      setOrder('DESC');
      setOrderBy(property);
    } else if (isDesc) {
      setOrder(null);
      setOrderBy(null);
    } else {
      setOrder('ASC');
      setOrderBy(property);
    }
  };

  const filterRows = (rows: Row[], filters: Filters): Row[] => {
    return rows.filter(row => {
      const rowDate = new Date(row.timestamp).setHours(0, 0, 0, 0); 
      const fromDate = filters.from ? new Date(filters.from).setHours(0, 0, 0, 0) : null; 
      const toDate = filters.to ? new Date(filters.to).setHours(23, 59, 59, 999) : null;
  
      return (
        (!filters.user || row.user.includes(filters.user)) &&
        (!filters.project || row.project.includes(filters.project)) &&
        (!filters.event || row.event.includes(filters.event)) &&
        (!fromDate || rowDate >= fromDate) &&
        (!toDate || rowDate <= toDate) &&
        (!filters.details || row.details.toLowerCase().includes(filters.details.toLowerCase()))
      );
    });
  };

  const getUniqueValues = (rows: Row[], key: keyof Row): string[] => {
    return Array.from(new Set(rows.map(row => row[key]))).filter(Boolean);
  };

  const uniqueDatesWithoutFilters = getUniqueValues(allData, 'timestamp')
  const uniqueUsers = getUniqueValues(rows, 'user');
  const uniqueProjects = getUniqueValues(rows, 'project');
  const uniqueEvents = getUniqueValues(rows, 'event');
  const filteredRows = filterRows(rows, filters);

  function sortTimestamps(timestamps: string[]): string[] {
    return timestamps.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
}
 
 const sortedTimestamps = sortTimestamps(uniqueDatesWithoutFilters)


  return (
    <LogbookContext.Provider value={{ 
      rows,
      sortedTimestamps,
      filteredRows,
      order,
      orderBy,
      handleSort,
      filters,
      setFilters,
      uniqueUsers,
      uniqueProjects,
      uniqueEvents,
      fetchData,
      localFilters,
      setLocalFilters,
      setWasFiltered,
      wasFiltered,
      fetchAllData,
      allData,
      loading
      }}>
      {children}
    </LogbookContext.Provider>
  );
};

const useLogbook = () => {
  const context = useContext(LogbookContext);
  if (!context) {
    throw new Error('useLogbook must be used within a LogbookProvider');
  }
  return context;
};

export { LogbookProvider, useLogbook };
